// Felder der Besucher-Masken: die festen Felder Name, E-Mail und Firma und die
// frei definierten Zusatzfelder (Telefon, Parkplatznummer, Besucherbegrüssung …).
//
// Historisch stehen die Zusatzfelder kundenweit als JSON in
// `module.add_in_fields` (Tabelle `module`, als deprecated markiert). Neu wird
// die ganze Feldliste pro Standort in den Visitor-Modul-Settings unter
// `additional_fields` gepflegt; solange dort nichts gespeichert ist, gelten
// weiterhin die alten Felder. Die Struktur bleibt bewusst identisch, damit
// bereits erfasste `additional_information` an den Besuchern weiter passen:
//
//   { title, field_type, type, is_shown[], accepted_values[], field_id,
//     inserted_value }
//
// Dazu kommen:
//   system_key        "name" / "email" / "company" bei den festen Feldern. Ihr
//                     Wert steht in den Spalten des Besuchers, nie in
//                     `additional_information`. Die Liste enthält sie, damit
//                     Bezeichnung, Pflicht und Reihenfolge an einer Stelle
//                     gepflegt werden.
//   visible_for_tags  ist die Liste gefüllt, erscheint das Feld nur, wenn der
//                     Besucher einen dieser Tags trägt. Leer = immer sichtbar.
//   tag_rules         Abweichungen je Tag: [{ tags[], title?, usage? }], siehe
//                     `effectiveField()`.
//   prefill_outlook   nur am Namen-Feld: Outlook-Add-in übernimmt den Namen
//                     neuer Teilnehmer aus dem Outlook-Kontakt. Ohne Wert an,
//                     nur `false` schaltet ab — siehe `prefillsNameFromOutlook()`.
//
// `field_id` ist der feste Schlüssel eines Feldes. Fehlt er, wird er aus dem
// Titel gebildet, genau so wie das Backend den Teams-Platzhalter bildet
// (`field_key` in services/teams_card.py) — bestehende Platzhalter
// `{{zusatz.<key>}}` bleiben damit gültig.

export const ADDITIONAL_FIELDS_SETTINGS_KEY = "additional_fields";

export const FIELD_TYPE_OPTIONS = [
  { value: "text", label: "Text" },
  { value: "number", label: "Zahl" },
  { value: "tel", label: "Telefon" },
  { value: "dropdown", label: "Auswahlliste" },
  { value: "checkbox", label: "Checkbox" },
  { value: "toggle", label: "Schalter" },
  { value: "signature", label: "Unterschrift" },
];

// Verwendung je Kanal — statt getrennter Felder „sichtbar in" und „Pflichtfeld"
// wird pro Kanal EIN Wert gepflegt: aus, optional oder Pflicht.
//
// Auf die gespeicherte Struktur wirkt das so:
//   is_shown  enthält den Kanal, sobald er nicht „aus" ist
//   type      "" (nirgends Pflicht) · "required" (in beiden Kanälen Pflicht)
//             "required-add-in" (nur Terminerstellung) · "required-app" (nur
//             Check-in)
export const CHANNEL_APPOINTMENT = "Outlook";
export const CHANNEL_CHECKIN = "sklera";

export const FIELD_CHANNELS = [
  { value: CHANNEL_APPOINTMENT, label: "Terminerstellung" },
  { value: CHANNEL_CHECKIN, label: "Check-in" },
];

export const USAGE_OFF = "off";
export const USAGE_OPTIONAL = "optional";
export const USAGE_REQUIRED = "required";

export const USAGE_OPTIONS = [
  { value: USAGE_OFF, label: "Aus" },
  { value: USAGE_OPTIONAL, label: "Optional" },
  { value: USAGE_REQUIRED, label: "Pflicht" },
];

const USAGE_RANK = { [USAGE_OFF]: 0, [USAGE_OPTIONAL]: 1, [USAGE_REQUIRED]: 2 };

// ── Feste Felder ──────────────────────────────────────────────────────────

export const SYSTEM_NAME = "name";
export const SYSTEM_EMAIL = "email";
export const SYSTEM_COMPANY = "company";

// Vorgaben entsprechen dem bisherigen Verhalten der Masken: Name und E-Mail
// Pflicht bei der Terminerstellung, Firma optional.
const SYSTEM_FIELDS = [
  {
    system_key: SYSTEM_NAME,
    title: "Name",
    field_type: "text",
    is_shown: [CHANNEL_APPOINTMENT],
    type: "required-add-in",
  },
  {
    system_key: SYSTEM_EMAIL,
    title: "E-Mail",
    field_type: "email",
    is_shown: [CHANNEL_APPOINTMENT],
    type: "required-add-in",
  },
  {
    system_key: SYSTEM_COMPANY,
    title: "Firma",
    field_type: "text",
    is_shown: [CHANNEL_APPOINTMENT],
    type: "",
  },
];

export const isSystemField = (field) => !!field?.system_key;

/**
 * Übernimmt das Outlook-Add-in den Namen aus dem Outlook-Kontakt? Einstellung
 * am Namen-Feld (Einstellungen → Besucher → Felder → Name), ohne Wert an.
 */
export const prefillsNameFromOutlook = (fields) =>
  (fields || []).find((f) => f.system_key === SYSTEM_NAME)?.prefill_outlook !==
  false;

/**
 * Wählbare Verwendungen eines Feldes in einem Kanal. Der Name ist bei der
 * Terminerstellung immer Pflicht und am Check-in Pflicht oder aus — ohne Namen
 * gibt es keinen Badge, keine Liste und keine Einladung.
 */
export function usageOptionsFor(field, channel) {
  if (field?.system_key !== SYSTEM_NAME) return USAGE_OPTIONS;
  return channel === CHANNEL_APPOINTMENT
    ? USAGE_OPTIONS.filter((o) => o.value === USAGE_REQUIRED)
    : USAGE_OPTIONS.filter((o) => o.value !== USAGE_OPTIONAL);
}

// ── Pflicht und Verwendung ────────────────────────────────────────────────

/** Ist das Feld in diesem Kanal Pflicht? */
export function isRequiredFor(field, channel = CHANNEL_APPOINTMENT) {
  const type = field?.type || "";
  if (type === "required") return true;
  if (type === "required-add-in") return channel === CHANNEL_APPOINTMENT;
  if (type === "required-app") return channel === CHANNEL_CHECKIN;
  return false;
}

/** Verwendung des Feldes in einem Kanal: aus / optional / Pflicht. */
export function channelUsage(field, channel) {
  if (!(field?.is_shown || []).includes(channel)) return USAGE_OFF;
  return isRequiredFor(field, channel) ? USAGE_REQUIRED : USAGE_OPTIONAL;
}

/** Verwendung setzen und daraus `is_shown` + `type` neu ableiten. */
export function applyChannelUsage(field, channel, usage) {
  return applyUsages(field, {
    [CHANNEL_APPOINTMENT]: channelUsage(field, CHANNEL_APPOINTMENT),
    [CHANNEL_CHECKIN]: channelUsage(field, CHANNEL_CHECKIN),
    [channel]: usage,
  });
}

function applyUsages(field, usages) {
  const is_shown = FIELD_CHANNELS.map((c) => c.value).filter(
    (c) => usages[c] !== USAGE_OFF,
  );
  const appointmentRequired = usages[CHANNEL_APPOINTMENT] === USAGE_REQUIRED;
  const checkinRequired = usages[CHANNEL_CHECKIN] === USAGE_REQUIRED;

  const type =
    appointmentRequired && checkinRequired
      ? "required"
      : appointmentRequired
        ? "required-add-in"
        : checkinRequired
          ? "required-app"
          : "";

  return { ...field, is_shown, type };
}

/** Pflicht in irgendeinem Kanal — für Listen-Anzeigen. */
export const isRequiredField = (field) => !!(field?.type || "");

// ── Feld-Schlüssel ────────────────────────────────────────────────────────

/** Schlüssel aus dem Titel, identisch zu `field_key` im Backend. */
export const fieldKeyFromTitle = (title) =>
  String(title || "")
    .trim()
    .replace(/[^a-zA-Z0-9_]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toLowerCase();

/**
 * Gehören Konfiguration und gespeicherter Wert zum selben Feld? Über die
 * `field_id`, sobald beide eine haben, sonst über den Titel — ältere Werte an
 * Besuchern tragen oft keine `field_id`.
 */
export function isSameField(a, b) {
  if (!a || !b) return false;
  if (a.field_id && b.field_id) return a.field_id === b.field_id;
  return (a.title || "") === (b.title || "");
}

// ── Neue / normalisierte Felder ───────────────────────────────────────────

export const emptyAdditionalField = () => ({
  title: "",
  field_type: "text",
  type: "",
  visible_for_tags: [],
  tag_rules: [],
  is_shown: ["Outlook"],
  accepted_values: [],
  field_id: "",
  inserted_value: "",
});

const normalizeRule = (rule) => ({
  tags: Array.isArray(rule?.tags) ? rule.tags.filter(Boolean) : [],
  title: String(rule?.title || "").trim(),
  usage: rule?.usage && typeof rule.usage === "object" ? rule.usage : {},
});

const normalizeField = (field) => ({
  ...emptyAdditionalField(),
  ...field,
  title: String(field?.title || "").trim(),
  visible_for_tags: Array.isArray(field?.visible_for_tags)
    ? field.visible_for_tags.filter(Boolean)
    : [],
  tag_rules: Array.isArray(field?.tag_rules)
    ? field.tag_rules.map(normalizeRule).filter((r) => r.tags.length > 0)
    : [],
  is_shown: Array.isArray(field?.is_shown) ? field.is_shown : [],
  accepted_values: Array.isArray(field?.accepted_values)
    ? field.accepted_values.filter(Boolean)
    : [],
});

/**
 * Feste Schlüssel vergeben: feste Felder heissen wie ihr `system_key`, den
 * übrigen fehlt er nur, wenn sie aus der Zeit vor den Standort-Settings
 * stammen. Doppelte Schlüssel bekommen eine Nummer angehängt.
 */
export function withFieldIds(fields) {
  const taken = new Set(fields.map((f) => f.field_id).filter(Boolean));
  SYSTEM_FIELDS.forEach((s) => taken.add(s.system_key));
  return fields.map((field) => {
    if (field.system_key) return { ...field, field_id: field.system_key };
    if (field.field_id) return field;
    const base = fieldKeyFromTitle(field.title) || "feld";
    let key = base;
    for (let n = 2; taken.has(key); n++) key = `${base}_${n}`;
    taken.add(key);
    return { ...field, field_id: key };
  });
}

/**
 * Ganze Feldliste eines Standorts, feste Felder eingeschlossen, in der
 * eingestellten Reihenfolge. `legacyFields` sind die kundenweiten Altfelder aus
 * `module_add_in_fields` — sie greifen, solange für den Standort nichts
 * gespeichert wurde, und werden mit dem ersten Speichern übernommen. Fehlt ein
 * festes Feld in der Liste, steht es mit seinen Vorgaben vorne.
 */
export function resolveVisitorFields(settings, legacyFields) {
  const stored = settings?.[ADDITIONAL_FIELDS_SETTINGS_KEY];
  const source = Array.isArray(stored)
    ? stored
    : Array.isArray(legacyFields)
      ? legacyFields
      : [];

  const fields = source
    .filter((f) => f && (f.title || f.system_key))
    .map((f) => {
      if (!f.system_key) return normalizeField(f);
      const preset = SYSTEM_FIELDS.find((s) => s.system_key === f.system_key);
      if (!preset) return null;
      // Typ und Schlüssel der festen Felder sind nicht einstellbar.
      const field = normalizeField({
        ...preset,
        ...f,
        title: f.title || preset.title,
        field_type: preset.field_type,
      });
      // Der Name ist bei der Terminerstellung immer Pflicht, auch wenn etwas
      // anderes gespeichert wurde, und nie an Tags gebunden.
      return f.system_key === SYSTEM_NAME
        ? applyChannelUsage(
            { ...field, visible_for_tags: [] },
            CHANNEL_APPOINTMENT,
            USAGE_REQUIRED,
          )
        : field;
    })
    .filter(Boolean);

  const missing = SYSTEM_FIELDS.filter(
    (s) => !fields.some((f) => f.system_key === s.system_key),
  ).map((s) => normalizeField(s));

  return withFieldIds([...missing, ...fields]);
}

/** Nur die Zusatzfelder, ohne Name, E-Mail und Firma. */
export function resolveAdditionalFields(settings, legacyFields) {
  return resolveVisitorFields(settings, legacyFields).filter(
    (f) => !isSystemField(f),
  );
}

/** Wurde für diesen Standort schon etwas gespeichert? */
export const hasStoredAdditionalFields = (settings) =>
  Array.isArray(settings?.[ADDITIONAL_FIELDS_SETTINGS_KEY]);

// ── Tags ──────────────────────────────────────────────────────────────────

/**
 * Ist das Feld für diese Tag-Auswahl sichtbar?
 *
 * Ohne hinterlegte Tags gilt es immer; sonst muss der Besucher mindestens einen
 * der Tags tragen (z.B. „Parkplatznummer" nur bei „Handwerker").
 */
export function isFieldVisibleForTags(field, selectedTagLabels) {
  const required = field?.visible_for_tags || [];
  if (required.length === 0) return true;
  const selected = selectedTagLabels || [];
  return required.some((tag) => selected.includes(tag));
}

/**
 * Das Feld, wie es für diese Tag-Auswahl gilt.
 *
 * Eine Regel greift, sobald der Besucher einen ihrer Tags trägt. Bezeichnung:
 * die erste greifende Regel mit eigener Bezeichnung. Verwendung je Kanal: die
 * strengste unter den greifenden Regeln, die für den Kanal etwas festlegen;
 * legt keine etwas fest, gilt die Grundeinstellung.
 *
 * Der `title` bleibt unverändert, er ist der Schlüssel der gespeicherten Werte.
 * Angezeigt wird `label`.
 */
export function effectiveField(field, selectedTagLabels) {
  const selected = selectedTagLabels || [];
  const matching = (field?.tag_rules || []).filter((rule) =>
    (rule.tags || []).some((tag) => selected.includes(tag)),
  );
  const label = matching.find((r) => r.title)?.title || field.title;
  if (matching.length === 0) return { ...field, label };

  const usages = {};
  FIELD_CHANNELS.forEach(({ value: channel }) => {
    const ruled = matching
      .map((r) => r.usage?.[channel])
      .filter((u) => u in USAGE_RANK)
      // Was das Feld nicht zulässt (Name aus bei der Terminerstellung), zählt
      // nicht — auch wenn es so gespeichert wurde.
      .filter((u) =>
        usageOptionsFor(field, channel).some((o) => o.value === u),
      );
    usages[channel] = ruled.length
      ? ruled.reduce((a, b) => (USAGE_RANK[b] > USAGE_RANK[a] ? b : a))
      : channelUsage(field, channel);
  });

  return { ...applyUsages(field, usages), label };
}

const asChannels = (channel) => (Array.isArray(channel) ? channel : [channel]);

/**
 * Pflicht in einem der Kanäle? `channel` darf ein Kanal oder eine Liste sein —
 * die Bearbeiten-Maske zeigt nach dem Check-in beide.
 */
export const isRequiredInAny = (field, channel) =>
  asChannels(channel).some((c) => isRequiredFor(field, c));

/**
 * Felder, die ein Besucher mit dieser Tag-Auswahl im Kanal sieht — mit der für
 * ihn gültigen Bezeichnung und Pflicht. Bei mehreren Kanälen genügt einer.
 */
export function visibleFieldsFor(fields, selectedTagLabels, channel) {
  const channels = asChannels(channel);
  return (fields || [])
    .filter((f) => isFieldVisibleForTags(f, selectedTagLabels))
    .map((f) => effectiveField(f, selectedTagLabels))
    .filter((f) => channels.some((c) => channelUsage(f, c) !== USAGE_OFF));
}

// ── Werte ─────────────────────────────────────────────────────────────────

/**
 * Ist eine Checkbox / ein Schalter an? Die Masken speichern "on"/"off", andere
 * Apps teils `true` oder "true" — alles gilt als angehakt.
 */
export const isCheckedValue = (value) =>
  value === true || value === "on" || value === "true";

/**
 * Wert eines Feldes am Besucher. Feste Felder lesen die Spalte, Zusatzfelder
 * den passenden Eintrag aus `additional_information`.
 */
export function fieldValue(field, visitor) {
  if (field.system_key) return visitor?.[field.system_key] ?? "";
  return (
    (visitor?.additional_information || []).find((f) => isSameField(f, field))
      ?.inserted_value ?? ""
  );
}

/** Pflichtfelder, die bei diesem Besucher im Kanal noch leer sind. */
export function missingRequiredFields(fields, visitor, channel) {
  return visibleFieldsFor(fields, visitor?.tag_labels, channel)
    .filter((f) => isRequiredInAny(f, channel))
    .filter((f) => {
      const value = String(fieldValue(f, visitor) ?? "").trim();
      // Ein Schalter oder eine Checkbox ist erst mit „an" ausgefüllt.
      if (f.field_type === "checkbox" || f.field_type === "toggle") {
        return !isCheckedValue(value);
      }
      return value === "";
    });
}

/**
 * Wert eines Zusatzfeldes in `additional_information` setzen. Gibt es für das
 * Feld noch keinen Eintrag (neu konfiguriert, nachdem der Besucher angelegt
 * wurde), wird er aus der Konfiguration ergänzt.
 */
export function setAdditionalValue(additionalInformation, field, value) {
  const existing = additionalInformation || [];
  if (existing.some((f) => isSameField(f, field))) {
    return existing.map((f) =>
      isSameField(f, field)
        ? { ...f, field_id: f.field_id || field.field_id, inserted_value: value }
        : f,
    );
  }
  return [...existing, { ...toAdditionalInfo(field), inserted_value: value }];
}

/** Konfiguration ohne Angaben, die nur in die Settings gehören. */
export const toAdditionalInfo = (field) => {
  // eslint-disable-next-line no-unused-vars
  const { label, tag_rules, visible_for_tags, ...rest } = field;
  return rest;
};

/**
 * `additional_information` für das Speichern eines Besuchers
 * (`/user/change_visitor_data`). Das Backend ersetzt dabei ALLE Werte des
 * Besuchers — also immer die vollständige Liste schicken. Ältere Einträge
 * haben teils kein `is_shown` oder einen leeren Wert; beides lehnt das Backend
 * sonst ab.
 */
export const toAdditionalInfoPayload = (additionalInformation) =>
  (additionalInformation || [])
    .filter((f) => f && f.title)
    .map((f) => ({
      type: f.type ?? f.info_type ?? "",
      title: f.title,
      field_id: f.field_id || null,
      is_shown: Array.isArray(f.is_shown) ? f.is_shown : [],
      field_type: f.field_type || "text",
      inserted_value: String(f.inserted_value ?? ""),
      accepted_values: Array.isArray(f.accepted_values) ? f.accepted_values : [],
    }));
